/**
 * Persistent media-contract-v2 player — one media element for the whole session.
 *
 * Live playlists publish honest EXT-X-GAP entries instead of waiting for every
 * rendition to be ready, so audio can play before video heals in; that alone
 * only flips the badge (the pure `mediaStateAt` over the coverage unions),
 * never the element. A finalized snapshot heals those gaps by rendering a
 * fresh `?fin=N` revision — the master's children change under it without the
 * master's own URL changing, so `snapshotRev` rides in as a prop: a change
 * reloads the source on this same element and resumes at the same UTC. That
 * is the one case the "never remount" rule yields to.
 *
 * Deliberately tiny compared to the legacy HLSPlayer: no camera fan-out (v2 sessions are
 * single-phone), no tier picker (renditions are canonical), no PDT re-anchoring (the
 * playlists carry PROGRAM-DATE-TIME from phone-attested UTC bounds).
 */

import Hls from 'hls.js';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import PlayerChrome, { GlassRailData, PlayerTransport } from '../PlayerChrome';
import { intersectRanges, utcToMediaSeconds } from './firstOverlap';
import { clampSeekSeconds } from './seekClamp';
import {
  AvailabilityBands,
  buildAvailabilityBands,
  mediaStateAt,
  MediaStateKind,
  TimeRange,
} from './availability';

/**
 * Imperative handle for hosts that drive the playhead from wall-clock UTC
 * (the synced viewer seeks here when the operator clicks the map or a chart).
 * Structurally identical to `HLSPlayerHandle` so a host can hold one ref for
 * either player.
 */
export interface V2PlayerHandle {
  /** Seek to the moment whose absolute UTC matches `utcMs`. */
  seekToUtc: (utcMs: number) => void;
  play: () => void;
  pause: () => void;
}

export interface V2PlayerProps {
  /** Absolute or root-relative v2 master URL (operator route or /share/{token}/v2/...). */
  masterUrl: string;
  /** Independent readiness unions from the coverage endpoint (UTC ms pairs). */
  audioUnion?: TimeRange[];
  videoUnion?: TimeRange[];
  /**
   * Coverage's `snapshot_rev` — null until a finalized snapshot exists. A
   * change reloads this element's source in place (same UTC, same
   * paused/playing state, same volume) instead of leaving the healed media
   * unreachable behind a master URL that never changes.
   */
  snapshotRev?: number | null;
  autoPlay?: boolean;
  muted?: boolean;
  className?: string;
  /** Playhead as UTC ms (from the playlist PROGRAM-DATE-TIME), null until known. */
  onUtcUpdate?: (utcMs: number | null) => void;
  /** Fires once the timeline's first PROGRAM-DATE-TIME is known; UTC ms. */
  onPdtReady?: (firstPdtMs: number) => void;
  /**
   * One-shot seek applied as soon as the timeline anchor is known — a deep
   * link, a saved resume position, or a lap/stage entry point. Re-applied if
   * the value changes, so a second entry point seeks again.
   */
  initialSeekUtcMs?: number | null;
  /**
   * Live session — a UTC seek outside the rolling window resumes near the live
   * edge instead of at the oldest buffered segment (see `clampSeekSeconds`).
   */
  live?: boolean;
  /** Fires only when the badge truth changes — never accompanied by a remount. */
  onMediaState?: (state: MediaStateKind) => void;
  /**
   * Fires once playback is judged unrecoverable for this `masterUrl` — a
   * fatal MEDIA/OTHER error, or a fatal NETWORK_ERROR (e.g. the master
   * 404s, which happens in normal operation whenever a v2 session's
   * playlist isn't reachable yet) that has exceeded its bounded retry
   * budget. Lets the parent stop treating this session as v2-playable
   * and show its own fallback/error UI instead of a spinner that would
   * otherwise retry `hls.startLoad()` forever.
   */
  onFatalError?: (detail: string) => void;
  /**
   * Set to mount the app's own chrome (playbar, glass rail, Maximise) via
   * `PlayerChrome`, so a v2 session is indistinguishable from a legacy one.
   * The value picks the same fullscreen posture `HLSPlayer` takes: `full`
   * reserves the controls (dashboard), `minimal` goes immersive and auto-hides
   * them (fan viewer). Omitted, the element keeps native browser controls —
   * right for bare embeds with no surrounding chrome to match.
   */
  chrome?: 'full' | 'minimal';
  /** Height-bound the picture without entering fullscreen (mobile landscape). */
  heightFit?: boolean;
  glassRail?: GlassRailData | null;
  /** Fullscreen a parent shell instead of the player; `maximized` drives the icon. */
  onMaximize?: () => void;
  maximized?: boolean;
  onControlsHiddenChange?: (hidden: boolean) => void;
}

/** Bounded retry budget for a fatal NETWORK_ERROR (e.g. a 404 master) —
 *  without a bound, hls.js's own recommended `startLoad()` retry on every
 *  fatal network error becomes an unbounded tight loop against a URL that
 *  will never start responding (default-off share-token flag, a session
 *  that never advertised v2, etc). */
const MAX_NETWORK_RETRIES = 3;

/** How long a seek can sit unresolved before the pending indicator gives way
 *  to a retryable message — long enough to cover normal segment fetch/decode
 *  stalls, short enough that a genuinely stuck seek doesn't look frozen. */
const SEEK_PENDING_TIMEOUT_MS = 10_000;

const V2Player = forwardRef<V2PlayerHandle, V2PlayerProps>(function V2Player(
  {
    masterUrl,
    audioUnion,
    videoUnion,
    snapshotRev = null,
    autoPlay = false,
    muted = false,
    live = false,
    className,
    onUtcUpdate,
    onPdtReady,
    initialSeekUtcMs,
    onMediaState,
    onFatalError,
    chrome,
    heightFit = false,
    glassRail = null,
    onMaximize,
    maximized = false,
    onControlsHiddenChange,
  },
  ref,
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  // What the mount effect last built its instance from — tells a brand new
  // session (different masterUrl: hard reset) apart from a healed finalized
  // snapshot on the SAME session (same masterUrl, new snapshotRev: seamless
  // reload).
  const previousMountRef = useRef<{ masterUrl: string; rev: number | null } | null>(null);
  const [mediaState, setMediaState] = useState<MediaStateKind>('gap');
  const [fatal, setFatal] = useState<string | null>(null);
  // Feedback for the gap between a seek and playback resuming — otherwise a
  // seek that takes a moment (or one hls.js can't resolve at all) looks like
  // the player just froze.
  const [seekPending, setSeekPending] = useState(false);
  const [seekTimedOut, setSeekTimedOut] = useState(false);

  const bands: AvailabilityBands = useMemo(
    () => buildAvailabilityBands({ audio: audioUnion ?? [], video: videoUnion ?? [] }),
    [audioUnion, videoUnion],
  );
  const bandsRef = useRef(bands);
  bandsRef.current = bands;
  // Raw unions (not the highest-availability bands) for the first-overlap
  // start seek below — read live inside the mount effect's closures without
  // making audioUnion/videoUnion retrigger that effect.
  const unionsRef = useRef({ audio: audioUnion ?? [], video: videoUnion ?? [] });
  unionsRef.current = { audio: audioUnion ?? [], video: videoUnion ?? [] };
  const callbacksRef = useRef({ onUtcUpdate, onPdtReady, onMediaState, onFatalError });
  callbacksRef.current = { onUtcUpdate, onPdtReady, onMediaState, onFatalError };
  const stateRef = useRef<MediaStateKind>('gap');
  const liveRef = useRef(live);
  liveRef.current = live;
  // UTC at media time 0, known from the playlist alone — a host can seek before
  // the first frame ever plays. `positionRef` is the last reported playhead UTC.
  // `anchorReady` mirrors the ref into render state purely so the initial-seek
  // effect below has something to depend on.
  const anchorRef = useRef<number | null>(null);
  const positionRef = useRef<number | null>(null);
  const [anchorReady, setAnchorReady] = useState(false);
  // The initialSeekUtcMs value already applied. anchorReady flips again on a
  // seamless reload (the new source needs its own fresh anchor); without this,
  // that second flip would replay a deep link the viewer has since moved past.
  const appliedSeekRef = useRef<number | null | undefined>(undefined);
  // One-shot per mount: starts playback at the first audio/video overlap
  // instead of media time 0 when the head of the timeline is audio-only.
  const overlapSeekAppliedRef = useRef(false);

  // Transport state, mirrored off the element so the shared chrome can render
  // it. Only populated when this player owns the chrome — with native controls
  // the browser reads the element directly.
  const [isPlaying, setIsPlaying] = useState(false);
  // Mirrors `isPlaying` synchronously so a reload can read "was it playing"
  // without waiting on React's own state batching.
  const isPlayingRef = useRef(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [canPlay, setCanPlay] = useState(false);
  const [chromeMuted, setChromeMuted] = useState(muted);
  const [volume, setVolume] = useState(0.8);
  // Fresh retry budget per hls.js instance — the effect below creates a new
  // one whenever masterUrl or snapshotRev changes.
  const networkRetriesRef = useRef(0);

  useImperativeHandle(
    ref,
    () => ({
      seekToUtc: (utcMs: number) => {
        const video = videoRef.current;
        if (!video || !Number.isFinite(utcMs)) return;
        const playhead = positionRef.current;
        let target: number;
        if (playhead !== null) {
          // Offset from the reported playhead rather than from the anchor: it stays
          // exact across EXT-X-DISCONTINUITY, where media time and wall time are no
          // longer one linear map.
          target = video.currentTime + (utcMs - playhead) / 1000;
        } else if (anchorRef.current !== null) {
          target = (utcMs - anchorRef.current) / 1000;
        } else {
          return;
        }
        video.currentTime = clampSeekSeconds(video, target, { live: liveRef.current, intent: 'explicit' });
      },
      play: () => {
        void videoRef.current?.play().catch(() => undefined);
      },
      pause: () => videoRef.current?.pause(),
    }),
    [],
  );

  // Mounts the source once per masterUrl; a snapshotRev change (a finalized
  // snapshot healed) reloads it in place on this same element instead —
  // the one case the persistent-element contract yields to. Everything else
  // that changes DURING the session (badge, unions) reuses this instance.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const previousMount = previousMountRef.current;
    const isRevisionReload =
      previousMount !== null &&
      previousMount.masterUrl === masterUrl &&
      previousMount.rev !== snapshotRev;
    previousMountRef.current = { masterUrl, rev: snapshotRev };

    // Capture exactly what a mid-watch viewer must not lose, before anything
    // below tears down the instance that is currently playing.
    const resume = isRevisionReload
      ? {
          utcMs: positionRef.current,
          paused: !isPlayingRef.current,
          muted: video.muted,
          volume: video.volume,
        }
      : null;

    setFatal(null);
    setSeekPending(false);
    setSeekTimedOut(false);
    networkRetriesRef.current = 0;
    // The healed/finalized playlist can start earlier than the live one it
    // replaces (it may carry more history), so media-time-0's UTC must be
    // recomputed even on a seamless reload — only the absolute UTC captured
    // above survives the swap, never the old anchor.
    anchorRef.current = null;
    positionRef.current = null;
    setAnchorReady(false);
    if (!isRevisionReload) {
      appliedSeekRef.current = undefined;
    }
    overlapSeekAppliedRef.current = false;

    // An old instance's queued hls.js event or pending retry must never act
    // on state a newer instance already owns.
    let destroyed = false;

    const noteAnchor = (utcMs: number) => {
      if (destroyed || anchorRef.current !== null || !Number.isFinite(utcMs)) return;
      anchorRef.current = utcMs;
      setAnchorReady(true);
      callbacksRef.current.onPdtReady?.(utcMs);
      if (resume) {
        if (resume.utcMs !== null) {
          video.currentTime = clampSeekSeconds(video, (resume.utcMs - utcMs) / 1000, {
            live: liveRef.current,
            intent: 'resume',
          });
        }
        video.muted = resume.muted;
        video.volume = resume.volume;
        if (!resume.paused) void video.play().catch(() => undefined);
      } else if (initialSeekUtcMs == null && !overlapSeekAppliedRef.current) {
        // Head-of-timeline audio-only gap: the browser only plays where audio
        // AND video buffers overlap, so starting at 0 spins forever with no
        // error. Jump to the first point both are actually available.
        const overlap = intersectRanges(unionsRef.current.audio, unionsRef.current.video)[0];
        if (overlap) {
          const seconds = utcToMediaSeconds(overlap[0], utcMs);
          if (seconds > 0) {
            overlapSeekAppliedRef.current = true;
            video.currentTime = seconds;
            console.debug(`v2 player: starting at first audio/video overlap (+${seconds.toFixed(1)}s)`);
          }
        }
      }
    };
    // Native HLS (Safari) publishes the timeline's start date on the element; in
    // Chrome `getStartDate` is absent and the hls.js handler below supplies it.
    const noteNativeAnchor = () => {
      const native = video as HTMLVideoElement & { getStartDate?: () => Date };
      const start = native.getStartDate?.();
      if (start instanceof Date && Number.isFinite(start.getTime())) {
        noteAnchor(start.getTime());
      }
    };
    video.addEventListener('loadedmetadata', noteNativeAnchor);

    // Element state the shared chrome renders. `durationchange` matters as much
    // as `loadedmetadata` here: a live playlist's duration grows as cells are
    // appended, and a seek bar pinned to the first-known length would refuse to
    // reach the newest media.
    const syncTransport = () => {
      setPosition(video.currentTime);
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    };
    const onPlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
    };
    const onPause = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
    };
    const onCanPlay = () => setCanPlay(true);
    video.addEventListener('timeupdate', syncTransport);
    video.addEventListener('durationchange', syncTransport);
    video.addEventListener('loadedmetadata', syncTransport);
    video.addEventListener('seeked', syncTransport);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('canplay', onCanPlay);

    // Feedback for the gap between a seek and playback resuming. `seeking`
    // opens the window, `seeked`/`canplay` close it; a stuck seek (network
    // stall, an hls.js retry loop) falls back to a retryable message instead
    // of leaving the spinner up forever.
    let seekTimeoutId: number | null = null;
    const clearSeekTimeout = () => {
      if (seekTimeoutId !== null) {
        window.clearTimeout(seekTimeoutId);
        seekTimeoutId = null;
      }
    };
    const onSeekResolved = () => {
      clearSeekTimeout();
      setSeekPending(false);
      setSeekTimedOut(false);
    };
    const onSeeking = () => {
      setSeekTimedOut(false);
      setSeekPending(true);
      clearSeekTimeout();
      seekTimeoutId = window.setTimeout(() => {
        seekTimeoutId = null;
        setSeekPending(false);
        setSeekTimedOut(true);
      }, SEEK_PENDING_TIMEOUT_MS);
    };
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeekResolved);
    video.addEventListener('canplay', onSeekResolved);

    const reportPosition = () => {
      let utcMs: number | null = null;
      const hls = hlsRef.current;
      const playing = hls?.playingDate;
      if (playing instanceof Date && Number.isFinite(playing.getTime())) {
        utcMs = playing.getTime();
      } else {
        // Native HLS (Safari): EXT-X-PROGRAM-DATE-TIME surfaces via getStartDate(), which
        // lib.dom does not declare on every TS target — feature-detect it.
        const native = video as HTMLVideoElement & { getStartDate?: () => Date };
        const start = native.getStartDate?.();
        if (start instanceof Date && Number.isFinite(start.getTime())) {
          utcMs = start.getTime() + video.currentTime * 1000;
        }
      }
      positionRef.current = utcMs;
      callbacksRef.current.onUtcUpdate?.(utcMs);
      if (utcMs !== null) {
        noteAnchor(utcMs - video.currentTime * 1000);
        const next = mediaStateAt(utcMs, bandsRef.current);
        if (next !== stateRef.current) {
          stateRef.current = next;
          setMediaState(next);
          callbacksRef.current.onMediaState?.(next);
        }
      }
    };
    video.addEventListener('timeupdate', reportPosition);

    if (Hls.isSupported()) {
      const hls = new Hls({
        // Cookie-session auth — the v2 master/audio/video playlist,
        // media-segment, and init-segment routes are gated by the session's event
        // (operator bearer, owning device, or the anonymous share/playback gate —
        // see app/api/v2_playback.py's route guards). hls.js issues its own XHRs
        // outside axios/fetch's credential config, so it needs the same opt-in
        // HLSPlayer.tsx already uses: withCredentials=true makes the browser
        // attach the (HttpOnly) session cookie to these same-origin XHRs too. No
        // CSRF header needed — every request hls.js makes here is a GET
        // (playlists/segments/init), a safe method services/auth/csrf.py never
        // gates. Anonymous viewers have no session cookie at all, so this is a
        // harmless no-op for them — same posture the old bearer injection had.
        xhrSetup: (xhr) => {
          xhr.withCredentials = true;
        },
        enableWorker: true,
        lowLatencyMode: false,
        // Same no-force-live posture as the legacy player (operator-requested).
        liveSyncDurationCount: 999,
        liveMaxLatencyDurationCount: 10000,
        startPosition: 0,
        // Gap cells are first-class in v2 (EXT-X-GAP); let hls.js walk across them instead of
        // stalling at the first sound-only stretch.
        maxBufferHole: 6,
      });
      hlsRef.current = hls;
      hls.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
        if (destroyed) return;
        // First fragment's PROGRAM-DATE-TIME minus its media-timeline start = UTC at
        // media time 0. Known from the playlist alone, so a host seek works before
        // playback has ever produced a `timeupdate`.
        const first = data.details?.fragments?.[0];
        if (first && typeof first.programDateTime === 'number') {
          noteAnchor(first.programDateTime - first.start * 1000);
        }
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (destroyed || !data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          networkRetriesRef.current += 1;
          if (networkRetriesRef.current > MAX_NETWORK_RETRIES) {
            // Retry budget exhausted (e.g. the master 404s and keeps 404ing) —
            // stop hammering it and tell the parent this session isn't
            // v2-playable right now, instead of retrying forever behind a
            // silent spinner.
            const message = data.details ?? 'network error';
            hls.stopLoad();
            setFatal(message);
            onSeekResolved();
            callbacksRef.current.onFatalError?.(message);
          } else {
            hls.startLoad();
          }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          const message = data.details ?? 'playback failed';
          setFatal(message);
          onSeekResolved();
          callbacksRef.current.onFatalError?.(message);
        }
      });
      hls.loadSource(masterUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = masterUrl; // native Safari — the first device gate proves this path
    } else {
      setFatal('HLS is not supported in this browser');
      onSeekResolved();
    }
    // A revision reload restores play state itself once the new anchor lands
    // (see `noteAnchor` above) — autoPlay only applies to a genuinely fresh mount.
    if (!isRevisionReload && autoPlay) {
      void video.play().catch(() => undefined);
    }

    return () => {
      destroyed = true;
      clearSeekTimeout();
      video.removeEventListener('timeupdate', reportPosition);
      video.removeEventListener('loadedmetadata', noteNativeAnchor);
      video.removeEventListener('timeupdate', syncTransport);
      video.removeEventListener('durationchange', syncTransport);
      video.removeEventListener('loadedmetadata', syncTransport);
      video.removeEventListener('seeked', syncTransport);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeekResolved);
      video.removeEventListener('canplay', onSeekResolved);
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [masterUrl, autoPlay, snapshotRev]);

  // Entry-point seek. Anchored (not playhead-relative) on purpose: this runs
  // before playback has produced a position, and the anchor is the only truth
  // available then. Applies once per distinct value — anchorReady also flips
  // during a seamless revision reload, which must not replay a deep link the
  // viewer has since moved past.
  useEffect(() => {
    if (initialSeekUtcMs == null || !anchorReady) return;
    if (appliedSeekRef.current === initialSeekUtcMs) return;
    const video = videoRef.current;
    const anchor = anchorRef.current;
    if (!video || anchor === null) return;
    appliedSeekRef.current = initialSeekUtcMs;
    video.currentTime = clampSeekSeconds(video, (initialSeekUtcMs - anchor) / 1000, {
      live: liveRef.current,
      intent: 'resume',
    });
  }, [initialSeekUtcMs, anchorReady]);

  // Volume/mute live on the element only while the shared chrome owns them;
  // with native controls the browser mutates the element itself and mirroring
  // React state onto it would fight the user.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !chrome) return;
    video.muted = chromeMuted;
    video.volume = volume;
  }, [chrome, chromeMuted, volume]);

  const overlays = (
    <>
      {mediaState === 'audio' && (
        <div className="pointer-events-none absolute left-2 top-2 rounded bg-cyan-500/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-black">
          Live sound
        </div>
      )}
      {fatal && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-4 text-center text-xs text-white">
          {fatal}
        </div>
      )}
      {!fatal && seekPending && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Spinner size={24} />
        </div>
      )}
      {!fatal && seekTimedOut && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/70 p-4 text-center text-xs text-white">
          still working on that — try seeking again
        </div>
      )}
    </>
  );

  if (!chrome) {
    return (
      <div className={cn('relative h-full w-full bg-black', className)}>
        <video
          ref={videoRef}
          className="h-full w-full object-contain"
          controls
          muted={muted}
          playsInline
        />
        {overlays}
      </div>
    );
  }

  const transport: PlayerTransport = {
    isPlaying,
    currentTime: position,
    duration,
    ready: canPlay,
    muted: chromeMuted,
    volume,
    togglePlay: () => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) void video.play().catch(() => undefined);
      else video.pause();
    },
    seek: (seconds) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = clampSeekSeconds(video, seconds, { live: liveRef.current, intent: 'explicit' });
      setPosition(video.currentTime);
    },
    setMuted: setChromeMuted,
    setVolume: (next) => {
      setVolume(next);
      if (next > 0 && chromeMuted) setChromeMuted(false);
    },
  };

  return (
    <PlayerChrome
      transport={transport}
      variant={chrome}
      heightFit={heightFit}
      glassRail={glassRail}
      onMaximize={onMaximize}
      isMaximized={maximized}
      onControlsHiddenChange={onControlsHiddenChange}
    >
      {({ fitTiles }) => (
        <div className={cn('relative flex-grow bg-black', fitTiles && 'min-h-0', className)}>
          <video ref={videoRef} className="h-full w-full object-contain" playsInline />
          {overlays}
        </div>
      )}
    </PlayerChrome>
  );
});

export default V2Player;
